import React, { useMemo, useState, useEffect } from "react";
import { Input } from "@/components/ui/input";

/**
 * Brand color picker with HEX input, presets, live sample, and contrast check.
 */
const PRIMARY_PRESETS = ["#0f0f10", "#1A1A1A", "#111827", "#1E3A8A", "#065F46", "#7C2D12", "#4C1D95", "#831843"];
const ACCENT_PRESETS  = ["#D95D39", "#F59E0B", "#10B981", "#0EA5E9", "#6366F1", "#DB2777", "#DC2626", "#F97316"];

function hexIsValid(v) { return /^#([0-9a-f]{6})$/i.test(v || ""); }
function relLuminance(hex) {
  const m = /^#([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec(hex || "");
  if (!m) return 0;
  const toLin = (c) => {
    const v = parseInt(c, 16) / 255;
    return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
  };
  return 0.2126 * toLin(m[1]) + 0.7152 * toLin(m[2]) + 0.0722 * toLin(m[3]);
}
function contrastRatio(a, b) {
  const la = relLuminance(a); const lb = relLuminance(b);
  const lo = Math.min(la, lb); const hi = Math.max(la, lb);
  return (hi + 0.05) / (lo + 0.05);
}
function accessibleTextColor(bg) {
  return contrastRatio(bg, "#ffffff") >= 4.5 ? "#ffffff" : "#111827";
}

function Swatch({ hex, current, onSelect, testId }) {
  const active = hex.toLowerCase() === (current || "").toLowerCase();
  return (
    <button type="button" onClick={() => onSelect(hex)} data-testid={testId}
      className={`w-8 h-8 rounded-md border-2 transition ${active ? "border-zinc-900 scale-110" : "border-transparent hover:scale-105"}`}
      style={{ background: hex }} title={hex} aria-label={hex}/>
  );
}

function ColorField({ label, hint, value, onChange, presets, testIdPrefix, resetTo }) {
  const [txt, setTxt] = useState(value || "");
  useEffect(() => { setTxt(value || ""); }, [value]);
  const invalid = txt.length > 0 && !hexIsValid(txt);
  return (
    <div className="border rounded-xl p-4 bg-white">
      <div className="flex items-start justify-between mb-2">
        <div>
          <div className="text-sm font-medium">{label}</div>
          <div className="text-xs text-zinc-500 mt-0.5">{hint}</div>
        </div>
        <button type="button" onClick={() => onChange(resetTo)} className="text-[11px] text-zinc-500 hover:text-zinc-900" data-testid={`${testIdPrefix}-reset`}>
          Reset
        </button>
      </div>
      <div className="flex items-center gap-2 mt-3">
        <input type="color" value={hexIsValid(value) ? value : "#000000"} onChange={(e) => onChange(e.target.value)}
          className="w-11 h-11 rounded-md border border-zinc-200 cursor-pointer" aria-label={`${label} color picker`} data-testid={`${testIdPrefix}-picker`}/>
        <Input
          value={txt}
          onChange={(e) => { const v = e.target.value; setTxt(v); if (hexIsValid(v)) onChange(v); }}
          placeholder="#000000"
          maxLength={7}
          className={`w-32 mono ${invalid ? "border-red-400" : ""}`}
          data-testid={`${testIdPrefix}-hex`}
        />
        {invalid && <span className="text-[11px] text-red-600">Enter a #RRGGBB HEX.</span>}
      </div>
      <div className="mt-3 flex flex-wrap gap-1.5">
        {presets.map((p) => <Swatch key={p} hex={p} current={value} onSelect={onChange} testId={`${testIdPrefix}-swatch-${p.slice(1)}`}/>)}
      </div>
    </div>
  );
}

export default function ColorPicker({ primary, accent, onPrimary, onAccent }) {
  const primaryText = useMemo(() => accessibleTextColor(primary || "#000"), [primary]);
  const accentText = useMemo(() => accessibleTextColor(accent || "#000"), [accent]);
  const primaryContrast = useMemo(() => contrastRatio(primary || "#000", "#ffffff").toFixed(2), [primary]);
  const accentContrast = useMemo(() => contrastRatio(accent || "#000", "#ffffff").toFixed(2), [accent]);
  const primaryLowContrast = parseFloat(primaryContrast) < 4.5;
  const accentLowContrast = parseFloat(accentContrast) < 4.5;

  return (
    <div className="grid md:grid-cols-2 gap-4" data-testid="color-picker">
      <ColorField
        label="Primary color"
        hint="Used for navigation, primary surfaces, and major actions."
        value={primary} onChange={onPrimary}
        presets={PRIMARY_PRESETS} testIdPrefix="color-primary" resetTo="#1A1A1A"
      />
      <ColorField
        label="Accent color"
        hint="Used for highlights, active states, and secondary actions."
        value={accent} onChange={onAccent}
        presets={ACCENT_PRESETS} testIdPrefix="color-accent" resetTo="#D95D39"
      />

      <div className="md:col-span-2">
        <div className="text-xs uppercase tracking-widest text-zinc-500 mb-2">Live preview</div>
        <div className="border rounded-xl overflow-hidden">
          <div className="grid grid-cols-[220px_1fr]">
            {/* sidebar preview */}
            <div className="p-4" style={{ background: primary || "#111", color: primaryText }}>
              <div className="text-[10px] uppercase tracking-widest opacity-60">Workspace</div>
              <div className="font-semibold mt-1">Your Company</div>
              <div className="mt-4 space-y-1 text-sm">
                <div className="rounded px-2 py-1" style={{ background: `${accent}22` }}>Home</div>
                <div className="opacity-80 px-2 py-1">Virtual Office</div>
                <div className="opacity-80 px-2 py-1">People</div>
              </div>
            </div>
            {/* content preview */}
            <div className="p-6 bg-white">
              <div className="text-xs uppercase tracking-widest text-zinc-500">Live sample</div>
              <div className="flex flex-wrap items-center gap-2 mt-3">
                <button type="button" className="rounded-md px-3 py-1.5 text-sm font-medium" style={{ background: primary, color: primaryText }} data-testid="preview-primary-btn">Primary button</button>
                <button type="button" className="rounded-md px-3 py-1.5 text-sm font-medium" style={{ background: accent, color: accentText }} data-testid="preview-accent-btn">Accent button</button>
                <span className="text-xs rounded-full px-2 py-0.5 font-medium" style={{ background: `${accent}22`, color: accent }}>Selected</span>
                <span className="text-[10px] rounded-full px-2 py-0.5 font-semibold" style={{ background: "#10B98122", color: "#065F46" }}>Online</span>
              </div>
              <div className="text-[11px] text-zinc-500 mt-4 space-y-1">
                <div>Primary contrast on white: <b>{primaryContrast}:1</b>{primaryLowContrast && <span className="ml-1 text-amber-700">— low; foreground auto-adjusted for legibility.</span>}</div>
                <div>Accent contrast on white: <b>{accentContrast}:1</b>{accentLowContrast && <span className="ml-1 text-amber-700">— low; use sparingly for accent only.</span>}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
